export type MarkupType = 'FLAT' | 'PERCENTAGE' | null | undefined;

/** Match the server's cent rounding before currency conversion or VAT removal. */
export function applyPriceDiscount(gross: number, percent: number): number {
  const cents = Math.round(Number(gross.toFixed(2)) * 100);
  const basisPoints = Math.round(percent * 100);
  return Math.round(cents * (10000 - basisPoints) / 10000) / 100;
}

/**
 * A FIFO stock lot with its reconstructed cost legs, as returned by
 * `tradingProduct.exportPriceList`.
 *
 * FX policy ("freeze the cost, float the display"):
 *  - The COST basis is per-leg historical: the purchase leg in its PO currency
 *    plus the TRY landed allocation, convertible only with `frozenFxRates` —
 *    the snapshot frozen at the lot's receipt date (GRN fxRate overriding the
 *    PO-currency entry). This recovers the true original purchase cost (e.g.
 *    EUR 17.00) and matches the stored TradingProduct.price exactly.
 *  - Today's live FX is applied ONLY to express a finished base-currency price
 *    in other display currencies (see convertDisplay).
 */
export interface PriceLot {
  batchNumber: string;
  receivedDate: string | Date;
  availableQty: number;
  /** TRY total booked at receipt (purchase x frozen fx + landed). Reference only. */
  unitCost: number;
  purchaseUnitCost: number;
  purchaseCurrency: string;
  landedTry: number;
  frozenFxRates: Record<string, number>;
}

export interface ProductPricing {
  markupType: MarkupType;
  markupValue: number | null | undefined;
  kdvRate: number; // percent
  discountPercent?: number;
  fallbackPrice?: number | null; // product.price when no markup configured
}

/** True when a usable markup is configured (matching lotSellPrice's branches). */
function hasMarkup(p: ProductPricing): boolean {
  return (
    (p.markupType === 'FLAT' || p.markupType === 'PERCENTAGE') && p.markupValue != null
  );
}

/** Sell price for a known cost: markup applied, optional KDV. */
export function lotSellPrice(
  cost: number,
  p: ProductPricing,
  opts: { withKdv: boolean }
): number {
  let base: number;
  if (p.markupType === 'FLAT' && p.markupValue != null) base = cost + p.markupValue;
  else if (p.markupType === 'PERCENTAGE' && p.markupValue != null)
    base = cost * (1 + p.markupValue / 100);
  else if (p.fallbackPrice != null) {
    // The stored product price is KDV-INCLUSIVE (computeFinalPrice writes it
    // that way) — never add KDV on top of it again.
    return opts.withKdv ? p.fallbackPrice : p.fallbackPrice / (1 + (p.kdvRate ?? 0) / 100);
  } else base = cost;
  const gross = base * (1 + (p.kdvRate ?? 0) / 100);
  if (p.discountPercent) {
    const discounted = applyPriceDiscount(gross, p.discountPercent);
    return opts.withKdv ? discounted : discounted / (1 + (p.kdvRate ?? 0) / 100);
  }
  return opts.withKdv ? gross : base;
}

/** FIFO current lot: oldest available batch with qty > 0 (null if none).
 *  Array.prototype.sort is stable, so same-timestamp lots keep the server's
 *  (receivedDate, id) order and resolve to the same lot as the stored price. */
export function currentLot(lots: PriceLot[]): PriceLot | null {
  const avail = lots.filter((l) => l.availableQty > 0);
  if (avail.length === 0) return null;
  const sorted = [...avail].sort(
    (a, b) => new Date(a.receivedDate).getTime() - new Date(b.receivedDate).getTime()
  );
  return sorted[0] ?? null;
}

/** TRY-per-1-ccy rate from an FX map; TRY is always 1, else null if absent. */
function fxRate(fx: Record<string, number> | undefined, ccy: string): number | null {
  if (ccy === 'TRY') return 1;
  const r = fx?.[ccy];
  return r && r > 0 ? r : null;
}

/**
 * FROZEN LEG — a lot's true historical cost expressed in `currency`, using
 * only the lot's own receipt-date snapshot. Mirrors the server's
 * lotCostInCurrency (tradingProductPricing.ts) exactly:
 *   purchase (PO ccy) → TRY at frozen rate → + landed (TRY) → target at
 *   frozen rate. Returns null when the snapshot can't reach either leg —
 * callers must render a visible blank, never substitute a live rate.
 */
export function lotCostInCurrency(lot: PriceLot, currency: string): number | null {
  const purchaseRate = fxRate(lot.frozenFxRates, lot.purchaseCurrency);
  if (purchaseRate == null) return null;
  const totalTry = lot.purchaseUnitCost * purchaseRate + lot.landedTry;
  const targetRate = fxRate(lot.frozenFxRates, currency);
  if (targetRate == null) return null;
  return totalTry / targetRate;
}

/**
 * A lot's sell price in the pricing side's own base currency: frozen cost →
 * markup → KDV, all in base currency. This is the figure that must equal the
 * stored TradingProduct.price for the FIFO-current lot (parity-tested).
 *
 * No-markup sides short-circuit to the manually stored price WITHOUT touching
 * frozen FX: that price is already denominated in the base currency and needs
 * no conversion, so a lot with an unresolvable receipt-date snapshot must not
 * blank a price that never depended on it.
 */
export function lotSellPriceInBase(
  lot: PriceLot,
  p: ProductPricing,
  baseCurrency: string,
  opts: { withKdv: boolean }
): number | null {
  if (!hasMarkup(p) && p.fallbackPrice != null) {
    return lotSellPrice(0, p, opts); // takes the fallbackPrice branch; cost unused
  }
  const cost = lotCostInCurrency(lot, baseCurrency);
  if (cost == null) return null;
  return lotSellPrice(cost, p, opts);
}

/**
 * DISPLAY LEG — express a finished base-currency amount in another currency at
 * TODAY's rates. This is settlement/display conversion of a price, never part
 * of the cost basis. Returns null when a required rate is missing.
 */
export function convertDisplay(
  amount: number,
  from: string,
  to: string,
  todayFx: Record<string, number> | undefined
): number | null {
  if (from === to) return amount;
  const rFrom = fxRate(todayFx, from);
  const rTo = fxRate(todayFx, to);
  if (rFrom == null || rTo == null) return null;
  return (amount * rFrom) / rTo;
}
